import type { Ref } from 'vue'

/**
 * Destinos sugeridos y datos de contacto.
 *
 * Los leen cabecera, pie, home y checkout, así que viven aquí: una carga por
 * idioma y el último éxito se queda en memoria si la siguiente petición falla.
 */
export const useContentStore = defineStore('content', () => {
  const nuxtApp = useNuxtApp()
  const locale = (nuxtApp.$i18n as { locale: Ref<string> }).locale

  const suggestedDestinations: Ref<SuggestedDestination[]> = ref([])
  const support: Ref<CustomerServiceContent | null> = ref(null)

  /**
   * Idioma de los datos en memoria; `null` mientras no haya una carga correcta.
   * Se expone porque Pinia solo serializa al cliente el estado que el store
   * devuelve, y sin él la hidratación repetiría la petición.
   */
  const loadedLocale: Ref<string | null> = ref(null)

  /** Carga en curso, para que varios consumidores no disparen la misma petición. */
  let pending: Promise<void> | null = null
  let pendingLocale: string | null = null

  const fetchContent = async (requested: string) => {
    try {
      const [destinations, customerService] = await Promise.all([
        $fetch<SuggestedDestination[]>('/api-web/content/suggested-destinations', {
          query: { locale: requested }
        }),
        $fetch<CustomerServiceContent>('/api-web/content/customer-service', {
          query: { locale: requested }
        })
      ])

      // Si el idioma cambió mientras llegaba, esta respuesta ya no vale.
      if (requested !== locale.value) return

      suggestedDestinations.value = [...destinations].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      )
      support.value = customerService
      loadedLocale.value = requested
    } catch (error: unknown) {
      // Se conserva el último éxito; sin él la página sigue siendo usable.
      console.error('Error loading shared content', getErrorMessage(error))
    }
  }

  const load = async (): Promise<void> => {
    const requested = locale.value
    if (loadedLocale.value === requested) return

    if (!pending || pendingLocale !== requested) {
      pendingLocale = requested
      const request = fetchContent(requested).finally(() => {
        // Solo la petición vigente libera el hueco: si el idioma cambió a medias,
        // la anterior no debe borrar la que ya la sustituyó.
        if (pending === request) {
          pending = null
          pendingLocale = null
        }
      })
      pending = request
    }

    await pending
  }

  watch(locale, () => {
    load()
  })

  return {
    suggestedDestinations,
    support,
    loadedLocale,
    load
  }
})
